import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "./components/call-sheet/hollo-call-sheet";
import "./components/header/default/hollo-header-default";
import "./components/header/gradient-minimalist/hollo-header-gradient-minimalist";
import {
  endIcon,
  errorIcon,
  micIcon,
  micIconSvg,
  micSlashIcon,
  sendIcon,
  speakerIcon,
  sparklesIcon,
  successIcon,
  warningIcon,
  waveIcon,
  xIcon,
} from "./components/icons";
import "./components/landing/default/hollo-landing-default";
import "./components/landing/gradient-minimalist/hollo-landing-gradient-minimalist";
import "./components/language-menu/hollo-language-menu";
import "./components/live-card/hollo-live-card";
import "./components/profile-image/hollo-profile-image";
import { SOCKET_URL, USERS_BASE_URL } from "./config";
import { holloEmbedStyles } from "./styles/default/hollo-embed-styles";
import { holloEmbedStyles as holloEmbedStylesGradientMinimalist } from "./styles/gradient-minimalist/hollo-embed-styles";
import { customUserPpsStyles } from "./styles/gradient-minimalist/custom-user-pps-styles";
import { customUserMikeTorchiaStyles } from "./styles/gradient-minimalist/custom-user-mike-torchia-styles";
import { ToastController } from "./controllers/toast-controller";
import { NudgeController } from "./controllers/nudge-controller";
import { MediaController } from "./controllers/media-controller";
import { SocketController } from "./controllers/socket-controller";
import { ensureFonts, lockBodyScroll, portalToBody } from "./lib/dom";
import {
  ensurePlayableVideoUrl,
  formatServerIssueMessage,
  getUserDeviceId,
  generateNewDeviceId,
} from "./lib/helper-functionts";
import { getFileById } from "./apis/media-api";
import {
  getUserProfile,
  getLanguages,
  getPageGreeting,
  getChatSessionMessages,
} from "./apis";
// Socket handled by SocketController
import type { ServerEvent, UiMessage, User } from "./types/chat";
import type { Toast, ToastLevel } from "./types/ui";
import {
  AdditionalTypes,
  ChatWsAudioAction,
  ChatWsChunkType,
  ChatWsMessageAction,
  ChatWsVideoAction,
} from "./types/chat";
import { v4 as uuidv4 } from "uuid";

export let USERS_URL = USERS_BASE_URL;

/**
 * Normalizes embedStyle from API to a valid style value.
 */
function normalizeEmbedStyle(
  style?: string
): "default" | "gradient_minimalist" {
  if (!style) return "default";
  const normalized = String(style).trim().toLowerCase();
  if (normalized === "gradient_minimalist") {
    return "gradient_minimalist";
  }
  return "default";
}

// function normalizeEmbedStyle(style?: string): "default" | "gradient_minimalist" {

//   return "gradient_minimalist";
// }

@customElement("hollo-embed")
export class HolloEmbed extends LitElement {
  private socketUrl = SOCKET_URL;
  @property({ type: String, attribute: "agent-id" }) agentId =
    "prod-hollo-agent-xyz";

  @state() private isOpen = false;
  @state() private liveCharacter = false;
  @state() private isCalling = false;
  @state() private callSeconds = 0;
  @state() private typing = false;
  @state() private typingLocal = false;

  @state() private messages: UiMessage[] = [];
  private pendingByCustomId = new Map<string, number>();
  @state() private liveVideoSrc?: string; // blob: or data:
  @state() private liveLoop = false; // loop only for idle
  @state() private liveVideoLoading = false;
  @state() private liveVideoFailed = false;
  @state() private toasts: Toast[] = [];
  @state() private user: User | null = null;
  @state() private userLoadError: string | null = null;
  @state() private wsConnected = false;
  @state() private reconnecting = false;
  @state() private firstLoad = true;
  @state() private language = "en-US";
  @state() private showLangMenu = false;
  @state() private isGreetingReceived = false;
  @state() private currentVideoFor?: string;
  @state() private showConnBanner = false;
  // handled by NudgeController
  @state() private isMobile = false;

  private prevObjectUrl?: string; // revoke old blob URLs
  // socket handled by SocketController
  private callTimer?: number;
  private currentAudio?: HTMLAudioElement;
  // private currentVoiceId?: string;
  private onGlobalStopAudio?: (e: Event) => void;
  private hasPortaledToBody = false;
  private connBannerTimer?: number;
  private audioLoadingTimeouts = new Map<string, number>(); // Track timeouts for audio loading
  // nudge timers handled by NudgeController

  // Controllers
  private toastController = new ToastController(this);
  private nudgeController = new NudgeController(this);
  private mediaController = new MediaController(this);
  private socketController = new SocketController(this, {
    buildUrl: () => this.getSocketUrl(),
    onEvent: (e) => this.onServerEvent(e),
    onOpen: () => {
      this.wsConnected = true;
      this.reconnecting = false;
      this.firstLoad = false;
      // Clear any pending banner timer and hide banner on successful connect
      if (this.connBannerTimer) {
        try {
          window.clearTimeout(this.connBannerTimer);
        } catch {}
        this.connBannerTimer = undefined;
      }
      this.showConnBanner = false;
      if (this.liveCharacter && !this.liveVideoSrc) this.resetToIdleVideo();
      // Load previous messages for the current session (only for gradient minimalist, and only if not already loaded)
      // Messages are already loaded before socket connects for gradient minimalist, so skip here
      const embedStyle = normalizeEmbedStyle(this.user?.embedStyle);
      if (embedStyle === "gradient_minimalist" && this.messages.length === 0) {
        // Only load if we haven't loaded yet (fallback in case pre-load failed)
        this.loadChatSessionMessages();
      }
    },
    onClose: () => {
      this.wsConnected = false;
      this.reconnecting = true;
      this.firstLoad = false;
      // Don't show connection banner if there's a user load error
      if (this.userLoadError) return;
      // Start a grace-period timer before showing the disconnect banner
      if (!this.connBannerTimer) {
        this.connBannerTimer = window.setTimeout(() => {
          this.connBannerTimer = undefined;
          if (!this.wsConnected && !this.liveCharacter && !this.userLoadError) {
            this.showConnBanner = true;
            // Auto-dismiss after 5 seconds
            setTimeout(() => {
              if (this.showConnBanner) {
                this.showConnBanner = false;
              }
            }, 5000);
          }
        }, 2000);
      }
    },
    onError: () => {
      this.wsConnected = false;
      this.reconnecting = true;
      this.firstLoad = false;
      // Don't show connection banner if there's a user load error
      if (this.userLoadError) return;
      // Start a grace-period timer before showing the disconnect banner
      if (!this.connBannerTimer) {
        this.connBannerTimer = window.setTimeout(() => {
          this.connBannerTimer = undefined;
          if (!this.wsConnected && !this.liveCharacter && !this.userLoadError) {
            this.showConnBanner = true;
            // Auto-dismiss after 5 seconds
            setTimeout(() => {
              if (this.showConnBanner) {
                this.showConnBanner = false;
              }
            }, 5000);
          }
        }, 2000);
      }
    },
  });

  private languages: {
    code: string;
    name: string;
    flag_url: string;
    is_chat_default?: boolean;
  }[] = [];

  connectedCallback() {
    super.connectedCallback();
    // Track viewport size to toggle mobile behaviors
    try {
      this.updateIsMobile();
      window.addEventListener("resize", this.updateIsMobile, { passive: true });
    } catch {}
    // Portal to document.body to escape parent stacking contexts
    try {
      const doc = this.ownerDocument || document;
      if (!this.hasPortaledToBody) {
        this.hasPortaledToBody = true;
        portalToBody(this);
      }
      ensureFonts(doc);
    } catch {}
    window.addEventListener("keydown", this.onKeydown, true);
    window.addEventListener("mousedown", this.onOutside, true);
    // Pause our HTMLAudio when any other player starts
    this.onGlobalStopAudio = (ev: Event) => {
      const anyEv = ev as CustomEvent<{ id?: string }>;
      const srcId = anyEv.detail?.id;
      if (!srcId || srcId === "embed-audio") return;
      try {
        if (this.currentAudio && !this.currentAudio.paused) {
          this.currentAudio.pause();
        }
      } catch {}
    };
    window.addEventListener("hollo-stop-audio", this.onGlobalStopAudio, true);

    // Load profile, then languages, then set idle video once
    this.getUser()
      .then(async () => {
        await this.loadLanguages();
        const idleId = this.user?.botIdleFileId || "";
        if (idleId) this.loadVideoById(idleId, true, true);
        // Preload page greeting text for the nudge
        this.loadPageGreeting();

        // For gradient minimalist style, load messages before connecting socket
        // This ensures we set isGreetingReceived before socket connects to prevent duplicate greetings
        const embedStyle = normalizeEmbedStyle(this.user?.embedStyle);
        if (embedStyle === "gradient_minimalist") {
          await this.loadChatSessionMessages();
        }

        // Connect socket after loading messages (for gradient minimalist) to ensure greeting flag is set
        // For default style, connect immediately (messages not loaded)
        this.reconnecting = true;
        this.wsConnected = false;
        this.socketController.connect();
      })
      .catch(() => {
        // If user load fails, still connect socket
        this.reconnecting = true;
        this.wsConnected = false;
        this.socketController.connect();
      });
    // Delayed nudge to encourage clicking the chat
    this.nudgeController.schedule(3500, 7000);
  }

  disconnectedCallback() {
    window.removeEventListener("keydown", this.onKeydown, true);
    window.removeEventListener("mousedown", this.onOutside, true);
    if (this.onGlobalStopAudio)
      window.removeEventListener(
        "hollo-stop-audio",
        this.onGlobalStopAudio,
        true
      );
    this.socketController.disconnect();
    if (this.prevObjectUrl) URL.revokeObjectURL(this.prevObjectUrl);
    // Clear any timers for the nudge
    this.nudgeController.clearTimers();
    // Clear connection banner timer if any
    if (this.connBannerTimer) {
      try {
        window.clearTimeout(this.connBannerTimer);
      } catch {}
      this.connBannerTimer = undefined;
    }
    // Clear all audio loading timeouts
    this.audioLoadingTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.audioLoadingTimeouts.clear();
    try {
      window.removeEventListener(
        "resize",
        this.updateIsMobile as EventListener
      );
    } catch {}
    super.disconnectedCallback();
  }

  private updateIsMobile = () => {
    try {
      const mql = window.matchMedia("(max-width: 768px)");
      this.isMobile = mql.matches || window.innerWidth <= 768;
    } catch {
      this.isMobile = false;
    }
  };

  // WebSocket
  private setupSocket() {
    // Back-compat if called elsewhere
    this.socketController.connect();
  }

  private async getUser() {
    const result = await getUserProfile(this.agentId);
    this.user = result.user;
    this.userLoadError = result.error;
  }

  private getSocketUrl(): string {
    try {
      const url = new URL(this.socketUrl);
      url.searchParams.delete("token");
      url.searchParams.set("user_device_id", getUserDeviceId());
      url.searchParams.set("ai_user_id", this.agentId);
      url.searchParams.set("is_embed", "true");
      if (this.isGreetingReceived) {
        url.searchParams.set("skip_greeting", "true");
      }
      if (this.liveCharacter) url.searchParams.set("is_live_page", "true");
      else url.searchParams.delete("is_live_page");
      console.log("this is the url", url.toString());
      return url.toString();
    } catch {
      return this.socketUrl;
    }
  }

  private async loadLanguages() {
    const userId = this.user?.id;
    if (!userId) return;
    const data = await getLanguages(userId);
    this.languages = data;
    // Choose default from server if available
    const defaultLang = data.find((l) => l.is_chat_default) || data[0];
    if (defaultLang?.code) this.language = defaultLang.code;
    this.requestUpdate();
  }

  // Format error messages with better titles and user-friendly descriptions
  private formatErrorMessage(error: string): { title: string; description: string } {
    const errorLower = error.toLowerCase();
    
    // Agent not found
    if (errorLower.includes("couldn't find") || errorLower.includes("check the agent id") || errorLower.includes("404")) {
      return {
        title: "AI Rep Not Found",
        description: "We couldn't find an AI rep with this ID. Please verify the AI rep ID and try again."
      };
    }
    
    // Network errors
    if (errorLower.includes("network") || errorLower.includes("fetch") || errorLower.includes("connection")) {
      return {
        title: "Connection Error",
        description: "Unable to connect to the server. Please check your internet connection and try again."
      };
    }
    
    // Failed to load profile
    if (errorLower.includes("failed to load") || errorLower.includes("profile")) {
      return {
        title: "Unable to Load Profile",
        description: "We encountered an issue loading the AI rep profile. Please try again in a moment."
      };
    }
    
    // Generic fallback
    return {
      title: "Something Went Wrong",
      description: error || "An unexpected error occurred. Please try again later."
    };
  }

  // Load previous chat session messages (only for gradient minimalist style)
  private async loadChatSessionMessages() {
    // Only load messages for gradient minimalist style, not default style
    const embedStyle = normalizeEmbedStyle(this.user?.embedStyle);
    if (embedStyle !== "gradient_minimalist") {
      return; // Skip loading for default style
    }

    // Always try to load messages to determine if we should show chat page or landing page
    // If messages exist, we'll show chat page; if not, we'll show landing page

    try {
      const userDeviceId = getUserDeviceId();
      const result = await getChatSessionMessages(
        userDeviceId,
        this.agentId,
        1,
        50,
        false
      );

      if (result && result.items && result.items.length > 0) {
        // Convert API messages to UiMessage format and fetch audio URLs
        const loadedMessagesPromises = result.items
          .sort((a, b) => {
            // Sort by created_at to ensure chronological order
            const timeA = new Date(a.created_at).getTime();
            const timeB = new Date(b.created_at).getTime();
            return timeA - timeB;
          })
          .map(async (msg) => {
            // Determine message type based on content and is_human
            let type: ChatWsChunkType;
            if (msg.is_human) {
              type = msg.audio_file_id
                ? ChatWsChunkType.USER_VOICE_MESSAGE
                : ChatWsChunkType.USER_MESSAGE;
            } else {
              type = ChatWsChunkType.AI_RESPONSE;
            }

            const uiMessage: UiMessage = {
              id: msg.id,
              chat_session_id: msg.chat_session_id,
              user_id: msg.user_id,
              is_human: msg.is_human,
              content: msg.content,
              created_at: msg.created_at,
              type: type,
              custom_id: msg.custom_id,
            };

            // Handle audio files if present - fetch the audio URL
            if (msg.audio_file_id) {
              const audioUrl = await getFileById(msg.audio_file_id);
              if (audioUrl) {
                uiMessage.audioUrl = audioUrl;
                // Extract mimeType from data URL if available
                if (audioUrl.startsWith("data:")) {
                  uiMessage.mimeType = audioUrl.slice(5, audioUrl.indexOf(";"));
                }
              }
            }

            // Handle video files if present
            if (msg.video_file_id) {
              (uiMessage as any).video_file_id = msg.video_file_id;
            }

            return uiMessage;
          });
        
        const loadedMessages = await Promise.all(loadedMessagesPromises);

        // Identify and mark greeting messages (first AI message with no user messages before it)
        // This ensures greeting messages are filtered out when showing the landing page
        let hasUserMessageBefore = false;
        for (let i = 0; i < loadedMessages.length; i++) {
          const msg = loadedMessages[i];
          if (msg.is_human) {
            hasUserMessageBefore = true;
          } else if (!msg.is_human && !hasUserMessageBefore) {
            // First AI message with no user messages before it is a greeting
            (msg as any).additional_type = AdditionalTypes.AI_GREETING;
          }
        }

        // Only set messages if we don't already have messages (to avoid overwriting new messages)
        // This will automatically show chat page if messages exist, or landing page if empty
        if (this.messages.length === 0) {
          this.messages = loadedMessages;

          // If we loaded any messages, mark greeting as received to prevent showing it again
          // This ensures that when the socket connects, it won't send a new greeting
          if (loadedMessages.length > 0) {
            this.isGreetingReceived = true;
          }

          // For gradient_minimalist style, hide loading spinner once messages are loaded
          // (user should already be loaded at this point since messages load after user loads)
          if (this.user && !this.userLoadError) {
            this.firstLoad = false;
          }

          this.requestUpdate(); // Trigger re-render to show chat page if messages exist
        } else {
          // Merge with existing messages, avoiding duplicates
          const existingIds = new Set(this.messages.map((m) => m.id));
          const newMessages = loadedMessages.filter(
            (m) => !existingIds.has(m.id)
          );
          if (newMessages.length > 0) {
            this.messages = [...this.messages, ...newMessages].sort((a, b) => {
              const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
              const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
              return timeA - timeB;
            });

            // If we merged any messages, mark greeting as received
            this.isGreetingReceived = true;
          }
        }
      } else {
        // No messages found - explicitly set messages to empty array to show landing page
        if (this.messages.length === 0) {
          this.messages = [];
        }
        // Even if no messages were loaded, for gradient_minimalist style, hide loading spinner
        // once we've attempted to load messages (user should already be loaded)
        if (this.user && !this.userLoadError) {
          this.firstLoad = false;
        }
        // Trigger re-render to ensure landing page is shown when no messages exist
        this.requestUpdate();
      }
    } catch (error) {
      console.warn("Failed to load chat session messages:", error);
      // Silently fail - don't block the UI
      // Explicitly set messages to empty array on error to show landing page
      if (this.messages.length === 0) {
        this.messages = [];
      }
      // Even on error, if user is loaded, hide loading spinner for gradient_minimalist
      if (this.user && !this.userLoadError) {
        this.firstLoad = false;
      }
      // Trigger re-render to ensure landing page is shown on error
      this.requestUpdate();
    }
  }

  // ---- Server events
  private onServerEvent(e: ServerEvent) {
    if ("type" in e) {
      switch (e.type) {
        case ChatWsChunkType.TYPING_START:
          this.typing = true;
          this.typingLocal = false; // handoff to server event
          break;
        case ChatWsChunkType.TYPING_END:
          this.typing = false;
          this.typingLocal = false;
          break;

        case ChatWsChunkType.USER_MESSAGE:
        case ChatWsChunkType.USER_VOICE_MESSAGE: {
          const incoming = e as UiMessage;
          const cid = incoming.custom_id;
          if (cid && this.pendingByCustomId.has(cid)) {
            // Replace the optimistic user entry with the definitive one
            const idx = this.pendingByCustomId.get(cid)!;
            const prev = this.messages[idx];
            const merged: UiMessage = {
              ...prev,
              ...incoming,
              _optimistic: false,
              audioUrl: incoming.audioUrl ?? prev.audioUrl,
              mimeType: incoming.mimeType ?? prev.mimeType,
            };
            const next = [...this.messages];
            next[idx] = merged;
            this.messages = next;
            this.pendingByCustomId.delete(cid);
          } else {
            this.messages = [...this.messages, incoming];
          }
          break;
        }

        case ChatWsChunkType.AI_RESPONSE:
        case ChatWsChunkType.AI_GREETING: {
          if (
            e.additional_type === AdditionalTypes.AI_GREETING &&
            this.isGreetingReceived
          ) {
            return;
          }
          if (
            e.type === ChatWsChunkType.AI_RESPONSE ||
            (e.additional_type === AdditionalTypes.AI_GREETING &&
              !this.isGreetingReceived)
          ) {
            const incoming = e as UiMessage;
            incoming.loadingVideo = this.liveCharacter ? true : false;
            this.messages = [...this.messages, incoming];
            // Clear typing state when AI response is received
            this.typing = false;
            this.typingLocal = false;
          }

          // Do NOT replace the human message. Append and let UI group by custom_id
          this.isGreetingReceived = true;

          break;
        }

        case ChatWsChunkType.GET_VOICE_ID: {
          const fileId = e.content;
          const forId = e.chat_message_id;
          if (!forId) break; // Skip if no chat_message_id

          const idx = this.messages.findIndex((m) => m.id === forId);

          // Clear timeout since we received a response
          const timeoutId = this.audioLoadingTimeouts.get(forId);
          if (timeoutId) {
            clearTimeout(timeoutId);
            this.audioLoadingTimeouts.delete(forId);
          }

          if (idx >= 0) {
            const next = [...this.messages];
            next[idx] = {
              ...next[idx],
              loadingVoice: false,
            } as UiMessage;
            this.messages = next;
          }
          this.addVoiceMessageFromFileId(
            fileId,
            /*isHuman*/ false,
            e.custom_id,
            /*autoplay*/ true,
            /*forMessageId*/ forId
          );
          // If you still want the old separate autoplay, you can remove it now:
          // this.playVoiceById(fileId);
          break;
        }

        case ChatWsChunkType.GET_VIDEO_ID:
          {
            const idx = this.messages.findIndex(
              (m) => m.id === e.chat_message_id
            );
            if (idx >= 0) {
              const next = [...this.messages];
              // replace the message with a fresh copy carrying loadingVideo=false
              next[idx] = {
                ...next[idx],
                loadingVideo: false,
              } as UiMessage;
              this.messages = next;
            }
          }
          try {
            window.dispatchEvent(
              new CustomEvent("hollo-stop-audio", {
                detail: { id: "video-play" },
              })
            );
          } catch {}
          this.currentVideoFor = e.chat_message_id;
          this.loadVideoById(e.content, false, false);
          this.playLiveVideoNow();

          break;
      }
    } else if ("error_type" in e) {
      const level =
        String(e.error_type || "error").toLowerCase() === "warning"
          ? "warning"
          : "error";
      const message = formatServerIssueMessage(e);
      // Filter out message limit/quota warnings
      if (level === "warning" && /messages?\s+left/i.test(message)) {
        return; // Skip showing this toast
      }
      this.pushToast(level, message);
    }
  }

  private pushToast(level: ToastLevel, text: string) {
    this.toastController.push(level, text);
    this.toasts = this.toastController.toasts;
  }

  private handleShowToast = (
    e: CustomEvent<{ level: ToastLevel; text: string }>
  ) => {
    this.pushToast(e.detail.level, e.detail.text);
  };

  private dismissToast = (id: string) => {
    this.toastController.dismiss(id);
    this.toasts = this.toastController.toasts;
  };

  private async loadVideoById(fileId: string, force = false, isIdle = false) {
    if (!this.liveCharacter && !force) return;
    if (!fileId) return;
    this.liveVideoLoading = true;
    this.liveVideoFailed = false;
    // if botIdleFileId is not a uuid, then it is a url
    if (
      !fileId.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
      )
    ) {
      this.liveVideoSrc = fileId;
      this.liveLoop = !!isIdle;
      this.liveVideoLoading = false;
      this.liveVideoFailed = false;
      return;
    }

    const raw = await getFileById(fileId); // returns data URL (often octet-stream)
    if (!raw) {
      this.liveVideoSrc = undefined;
      this.liveLoop = !!isIdle;
      this.liveVideoLoading = false;
      this.liveVideoFailed = true;
      return;
    }

    const fixedUrl = await ensurePlayableVideoUrl(raw, fileId, this.pushToast);
    this.mediaController.setPrevObjectUrl(fixedUrl);

    this.liveVideoSrc = fixedUrl;
    this.liveLoop = !!isIdle;
    this.liveVideoLoading = false;
    this.liveVideoFailed = false;
  }

  private async loadIdleVideo() {
    await this.resetToIdleVideo();
  }

  private async resetToIdleVideo() {
    // Clear current video selection when video ends
    this.currentVideoFor = undefined;
    const idleId = this.user?.botIdleFileId;
    if (!idleId) {
      this.liveVideoSrc = undefined;
      this.liveLoop = false;
      return;
    }
    await this.loadVideoById(idleId, true, true);
  }

  // UI plumbing
  private onKeydown = (e: KeyboardEvent) => {
    if (!this.isOpen) return;
    if (e.key === "Escape") {
      if (this.showLangMenu) this.showLangMenu = false;
      else this.closePopup();
    }
  };
  private onOutside = (e: MouseEvent) => {
    if (!this.isOpen) return;
    const path = e.composedPath();
    if (!path.includes(this)) {
      this.closePopup();
      return;
    }
    const menu = this.shadowRoot?.querySelector("hollo-language-menu");
    const button = this.shadowRoot?.querySelector(".flag-btn");
    if (
      this.showLangMenu &&
      menu &&
      button &&
      !path.includes(menu) &&
      !path.includes(button)
    ) {
      this.showLangMenu = false;
    }
  };
  private openPopup() {
    // Prevent accidental autoplay when reopening
    if (this.messages?.length) {
      this.messages = this.messages.map((m) =>
        m?.autoplay ? { ...m, autoplay: false } : m
      );
    }
    // Stop any currently playing video when reopening (but don't interfere with audio)
    try {
      const live = this.shadowRoot?.querySelector("hollo-live-card");
      if (live && typeof live.stopCurrentVideo === "function") {
        live.stopCurrentVideo();
      }
      // Reset video state without affecting audio
      this.currentVideoFor = undefined;
    } catch {}
    this.isOpen = true;
    this.nudgeController.dismiss();
  }
  private closePopup() {
    // Stop any currently playing audio globally
    try {
      window.dispatchEvent(
        new CustomEvent("hollo-stop-audio", { detail: { id: "embed-close" } })
      );
    } catch {}
    // Stop any currently playing video when closing (but don't interfere with audio)
    try {
      const live = this.shadowRoot?.querySelector("hollo-live-card");
      if (live && typeof live.stopCurrentVideo === "function") {
        live.stopCurrentVideo();
      }
      // Reset video state without affecting audio
      this.currentVideoFor = undefined;
    } catch {}
    this.isOpen = false;
    this.endCall();
    this.showLangMenu = false;
    this.nudgeController.dismiss();
    // Clear autoplay flags so nothing autoplays on next open
    if (this.messages?.length) {
      this.messages = this.messages.map((m) =>
        m?.autoplay ? { ...m, autoplay: false } : m
      );
    }
  }
  private toggleLiveCharacter() {
    // Stop any currently playing audio globally
    this.mediaController.stopGlobalAudio("toggle-live");
    // Stop any currently playing video in live card
    this.mediaController.stopLiveCard(this.shadowRoot);
    // Clear any pending autoplay flags so nothing auto plays after toggle
    if (this.messages?.length) {
      this.messages = this.messages.map((m) =>
        m.autoplay ? { ...m, autoplay: false } : m
      );
    }
    this.currentVideoFor = undefined;

    this.liveCharacter = !this.liveCharacter;
    this.socketController.disconnect();
    this.setupSocket();
    if (this.liveCharacter) {
      // Load idle video but ensure it does not start playing automatically
      this.resetToIdleVideo().then(() => {
        setTimeout(() => this.mediaController.stopLiveCard(this.shadowRoot), 0);
      });
    }
  }

  // Call controls
  private startCall = () => {
    if (this.isCalling) return;
    // Stop all media (audio + video) before starting a call
    this.mediaController.stopGlobalAudio("start-call");
    this.mediaController.stopLiveCard(this.shadowRoot);
    // Clear any per-message video selection
    this.currentVideoFor = undefined;

    this.isCalling = true; // open the call UI immediately
    this.callSeconds = 0; // reset timer display to 0
  };
  private onCallConnected = () => {
    // Start the counter exactly when the call connects
    if (this.callTimer) {
      try {
        window.clearInterval(this.callTimer);
      } catch {}
    }
    this.callSeconds = 0;
    this.callTimer = window.setInterval(() => {
      this.callSeconds += 1;
      this.requestUpdate();
    }, 1000);
  };
  private endCall = () => {
    if (!this.isCalling) return;
    this.isCalling = false;
    if (this.callTimer) window.clearInterval(this.callTimer);
  };

  // Composer → socket
  private async handleSendText(e: CustomEvent<{ text: string }>) {
    const content = e.detail.text?.trim();
    if (!content) return;

    // Stop all media (audio + video) on new send
    this.mediaController.stopGlobalAudio("send-text");
    this.mediaController.stopLiveCard(this.shadowRoot);
    this.currentVideoFor = undefined;

    const custom_id = uuidv4();

    // 2a) optimistic bubble
    const optimistic: UiMessage = {
      id: custom_id, // provisional
      custom_id,
      is_human: true,
      content,
      created_at: Math.floor(Date.now() / 1000),
      _optimistic: true,
    };
    this.messages = [...this.messages, optimistic];
    this.pendingByCustomId.set(custom_id, this.messages.length - 1);

    // 2b) send to server
    this.socketController.sendMessage({
      action: ChatWsMessageAction.SEND_MESSAGE,
      content,
      output_language: this.language,
      custom_id,
    });
    // Start local loading until server emits typing
    this.typingLocal = true;
  }
  private handleSendAudio(
    e: CustomEvent<{ base64: string; mimeType: string }>
  ) {
    e.stopPropagation();
    const { base64, mimeType } = e.detail;
    if (!base64) return;

    // Stop all media (audio + video) on new send
    this.mediaController.stopGlobalAudio("send-audio");
    this.mediaController.stopLiveCard(this.shadowRoot);
    this.currentVideoFor = undefined;

    const custom_id = uuidv4();
    const dataUrl = `data:${mimeType};base64,${base64}`;

    // 3a) optimistic voice bubble
    const optimistic: UiMessage = {
      id: custom_id, // provisional
      custom_id,
      is_human: true,
      content: "", // transcript (if any) can come later from server
      audioUrl: dataUrl, // <-- key bit: this renders a player
      mimeType,
      created_at: Date.now().toString(),
      _optimistic: true,
    };
    this.messages = [...this.messages, optimistic];
    this.pendingByCustomId.set(custom_id, this.messages.length - 1);

    // 3b) send to server
    this.socketController.sendMessage({
      action: ChatWsMessageAction.SEND_AUDIO,
      file_base64: base64,
      output_language: this.language,
      custom_id,
    });
    // Start local loading until server emits typing
    this.typingLocal = true;
  }

  private async addVoiceMessageFromFileId(
    fileId: string,
    isHuman: boolean,
    custom_id?: string,
    autoplay = false,
    forMessageId?: string
  ) {
    const dataUrl = await getFileById(fileId);
    if (!dataUrl) {
      // Clear loading state if file loading fails
      if (forMessageId) {
        const idx = this.messages.findIndex((m) => m.id === forMessageId);
        if (idx >= 0) {
          const next = [...this.messages];
          next[idx] = { ...next[idx], loadingVoice: false } as UiMessage;
          this.messages = next;
        }
      }
      console.warn(`Failed to load audio file: ${fileId}`);
      return;
    }

    const msg: UiMessage = {
      id: uuidv4(),
      custom_id,
      is_human: isHuman,
      content: "",
      audioUrl: dataUrl,
      mimeType: dataUrl.startsWith("data:")
        ? dataUrl.slice(5, dataUrl.indexOf(";"))
        : "audio/mpeg",
      created_at: Date.now().toString(),
      autoplay,
      for_message_id: forMessageId, // 👈 NEW
    };
    this.messages = [...this.messages, msg];
  }

  private requestAudio(chatMessageId: string) {
    // Stop any currently playing video when audio is requested
    this.mediaController.stopLiveCard(this.shadowRoot);
    this.currentVideoFor = undefined;

    // ✅ If we already have an audio bubble for this text, just toggle it.
    const existing = this.messages.find(
      (m) => m.for_message_id === chatMessageId && m.audioUrl
    );
    if (existing) {
      window.dispatchEvent(
        new CustomEvent("hollo-toggle-audio", {
          detail: { forMessageId: chatMessageId },
        })
      );
      return; // do NOT call the server again
    }

    // Clear any existing timeout for this message
    const existingTimeout = this.audioLoadingTimeouts.get(chatMessageId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.audioLoadingTimeouts.delete(chatMessageId);
    }

    // otherwise request from server (and show spinner)
    this.socketController.requestAudioOrVideo({
      action: ChatWsAudioAction.GET_AUDIO,
      chat_message_id: chatMessageId,
      output_language: this.language,
    });

    // Update loading state and replace the message immutably
    const idx = this.messages.findIndex((m) => m.id === chatMessageId);
    if (idx >= 0) {
      const next = [...this.messages];
      next[idx] = { ...next[idx], loadingVoice: true } as UiMessage;
      this.messages = next;
    }

    // Set a timeout to clear loading state if no response is received (30 seconds)
    const timeoutId = window.setTimeout(() => {
      const msgIdx = this.messages.findIndex((m) => m.id === chatMessageId);
      if (msgIdx >= 0 && this.messages[msgIdx].loadingVoice) {
        const next = [...this.messages];
        next[msgIdx] = { ...next[msgIdx], loadingVoice: false } as UiMessage;
        this.messages = next;
        console.warn(`Audio loading timeout for message: ${chatMessageId}`);
      }
      this.audioLoadingTimeouts.delete(chatMessageId);
    }, 30000); // 30 seconds timeout

    this.audioLoadingTimeouts.set(chatMessageId, timeoutId);
  }

  private requestVideo(chatMessageId: string) {
    // Toggle off if same message is clicked again
    if (this.currentVideoFor && this.currentVideoFor === chatMessageId) {
      try {
        const live = this.shadowRoot?.querySelector("hollo-live-card");
        if (live && typeof live.stopCurrentVideo === "function")
          live.stopCurrentVideo();
      } catch {}
      this.currentVideoFor = undefined;
      return;
    }

    // Stop any existing video and audio globally
    this.mediaController.stopLiveCard(this.shadowRoot);
    this.mediaController.stopGlobalAudio("video-click");

    // Request new video for this message
    this.socketController.requestAudioOrVideo({
      action: ChatWsVideoAction.GET_VIDEO,
      chat_message_id: chatMessageId,
      output_language: this.language,
    });

    // Update loading state and replace the message immutably
    const idx = this.messages.findIndex((m) => m.id === chatMessageId);
    if (idx >= 0) {
      const next = [...this.messages];
      next[idx] = { ...next[idx], loadingVideo: true } as UiMessage;
      this.messages = next;
    }
    this.currentVideoFor = chatMessageId;
  }

  private toggleLangMenu = () => (this.showLangMenu = !this.showLangMenu);
  private selectLanguage = (code: string) => {
    this.language = code;
    this.showLangMenu = false;
  };
  // Start a new chat session by generating a new device_id (only for gradient minimalist style)
  private startNewChatSession = () => {
    // Only allow new chat session for gradient minimalist style
    const embedStyle = normalizeEmbedStyle(this.user?.embedStyle);
    if (embedStyle !== "gradient_minimalist") {
      return; // Skip for default style
    }

    // Generate a new device_id and cache it in localStorage
    generateNewDeviceId();

    // Clear current messages
    this.messages = [];
    this.pendingByCustomId.clear();

    // Reset greeting state so greeting is shown again
    this.isGreetingReceived = false;

    // Stop any ongoing media
    this.mediaController.stopGlobalAudio("new-session");
    this.mediaController.stopLiveCard(this.shadowRoot);
    this.currentVideoFor = undefined;

    // Reset typing states
    this.typing = false;
    this.typingLocal = false;

    // Clear all audio loading timeouts
    this.audioLoadingTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.audioLoadingTimeouts.clear();

    // Reconnect socket with the new device_id
    this.socketController.disconnect();
    this.socketController.connect();
  };
  // Fetch a page greeting to show in the nudge
  private async loadPageGreeting() {
    const userId = this.user?.id;
    if (!userId) return;
    const data = await getPageGreeting(userId);
    if (data?.content && data.content.trim()) {
      this.nudgeController.setText(data.content);
    }
  }
  // Start the live video playback immediately if available
  private playLiveVideoNow() {
    this.mediaController.playLiveVideo(this.shadowRoot);
  }
  private currentFlag(): string {
    const l = this.languages.find((x) => x.code === this.language);
    // Fallback to text globe if not loaded yet
    return l?.flag_url || "";
  }

  private renderTrigger() {
    if (this.isOpen && this.isMobile) return null;

    const embedStyle = normalizeEmbedStyle(this.user?.embedStyle);

    // For gradient_minimalist, show the new styled button
    if (embedStyle === "gradient_minimalist") {
      return html` <button
        type="button"
        class="hollo-trigger-gradient-minimalist ${this.isOpen
          ? "hollo-trigger--active"
          : ""}"
        @click=${() => (this.isOpen ? this.closePopup() : this.openPopup())}
        aria-label="Ask Our AI Rep"
      >
        ${sparklesIcon(14)}
        <span>Ask Our AI Rep</span>
      </button>`;
    }

    // Default style - show the original circular button
    return html` <button
      class="hollo-trigger ${this.isOpen ? "hollo-trigger--active" : ""}"
      @click=${() => (this.isOpen ? this.closePopup() : this.openPopup())}
      aria-label="Open chat"
    >
      <hollo-profile-image
        .fileId=${this.user?.currentProfileImage}
        .alt=${this.user?.firstName}
        .size=${74}
        .class="hollo-trigger"
        .rounded=${true}
      ></hollo-profile-image>
    </button>`;
  }

  private renderNudge() {
    // Nudge banner disabled
    return null;
  }

  private renderHeaderAndMenus() {
    if (!this.isOpen) return null;
    // Don't show header if there's a user load error
    if (this.userLoadError) return null;
    if (this.user && this.user.isEmbedEnabled === false) return null;
    const embedStyle = normalizeEmbedStyle(this.user?.embedStyle);
    
    // Check if chat is empty (for gradient-minimalist, show minimal header with just language button)
    let isChatEmpty = false;
    if (embedStyle === "gradient_minimalist") {
      const nonGreetingMessages = this.messages.filter((m) => {
        const msg = m as UiMessage & { additional_type?: string };
        return msg.additional_type !== AdditionalTypes.AI_GREETING;
      });
      isChatEmpty = nonGreetingMessages.length === 0;
    }

    // Safely construct fullName, handling undefined values
    const firstName = this.user?.firstName || "";
    const lastName = this.user?.lastName || "";
    const fullName = firstName && lastName 
      ? `${firstName} ${lastName}` 
      : firstName || lastName || "";

    const headerProps = {
      liveCharacter: this.liveCharacter,
      currentFlag: this.currentFlag(),
      showLangMenu: this.showLangMenu,
      twinPhoneNumber: this.user?.twinPhoneNumber,
      twinPhoneNumberFriendlyForm: this.user?.twinPhoneNumberFriendlyForm,
      fullName: fullName,
      profileImageId: this.user?.currentProfileImage,
      userLoading: this.firstLoad && !this.user && !this.userLoadError,
      minimalMode: isChatEmpty,
    };

    const headerComponent =
      embedStyle === "gradient_minimalist"
        ? html`<hollo-header-gradient-minimalist
            .liveCharacter=${headerProps.liveCharacter}
            .currentFlag=${headerProps.currentFlag}
            .showLangMenu=${headerProps.showLangMenu}
            .twinPhoneNumber=${headerProps.twinPhoneNumber}
            .twinPhoneNumberFriendlyForm=${headerProps.twinPhoneNumberFriendlyForm}
            .fullName=${headerProps.fullName}
            .profileImageId=${headerProps.profileImageId}
            .userLoading=${headerProps.userLoading}
            .minimalMode=${headerProps.minimalMode}
            @toggle-live=${this.toggleLiveCharacter}
            @toggle-language-menu=${this.toggleLangMenu}
            @start-call=${this.startCall}
            @toggle-menu=${this.closePopup}
            @add-action=${this.startNewChatSession}
          ></hollo-header-gradient-minimalist>`
        : html`<hollo-header-default
            .liveCharacter=${headerProps.liveCharacter}
            .currentFlag=${headerProps.currentFlag}
            .showLangMenu=${headerProps.showLangMenu}
            .twinPhoneNumber=${headerProps.twinPhoneNumber}
            .twinPhoneNumberFriendlyForm=${headerProps.twinPhoneNumberFriendlyForm}
            .fullName=${headerProps.fullName}
            .profileImageId=${headerProps.profileImageId}
            .userLoading=${headerProps.userLoading}
            @toggle-live=${this.toggleLiveCharacter}
            @toggle-language-menu=${this.toggleLangMenu}
            @start-call=${this.startCall}
            @toggle-menu=${this.closePopup}
          ></hollo-header-default>`;

    return html`
      ${headerComponent}
      ${this.showLangMenu
        ? html` <hollo-language-menu
            .languages=${this.languages}
            .selected=${this.language}
            .align=${"right"}
            @select-language=${(e: CustomEvent<{ code: string }>) =>
              this.selectLanguage(e.detail.code)}
            @close-language-menu=${() => (this.showLangMenu = false)}
          ></hollo-language-menu>`
        : null}
    `;
  }

  private renderConnectionBanner() {
    // Don't show connection banner if:
    // - During initial load
    // - Live character mode
    // - There's a user load error (error page is showing)
    if (
      (this.firstLoad && !this.user && !this.userLoadError) ||
      this.liveCharacter ||
      this.userLoadError
    )
      return null;
    if (this.showConnBanner) {
      return html`<div
        class="hollo-conn-banner hollo-conn-banner--error"
        role="alert"
      >
        Connection lost
      </div>`;
    }
    return null;
  }

  private renderLandingComponent(
    embedStyle: "default" | "gradient_minimalist",
    props: {
      live?: boolean;
      fileId?: string;
      alt?: string;
      fullName?: string;
      currentVideoFor?: string;
      profileImageId?: string;
      messages: UiMessage[];
      typing: boolean;
      webcallUrl?: string;
    }
  ) {
    const embedStyleValue = normalizeEmbedStyle(embedStyle);

    if (embedStyleValue === "gradient_minimalist") {
      return html`<hollo-landing-gradient-minimalist
        .live=${props.live ?? false}
        .fileId=${props.fileId ?? ""}
        .alt=${props.alt ?? ""}
        .fullName=${props.fullName ?? ""}
        .currentVideoFor=${props.currentVideoFor}
        .profileImageId=${props.profileImageId ?? ""}
        .messages=${props.messages}
        .typing=${props.typing}
        @send-text=${(e: CustomEvent<{ text: string }>) =>
          this.handleSendText(e)}
        @send-audio=${(e: CustomEvent<{ base64: string; mimeType: string }>) =>
          this.handleSendAudio(e)}
        @request-audio=${(e: CustomEvent<{ chatMessageId: string }>) =>
          this.requestAudio(e.detail.chatMessageId)}
        @request-video=${props.live
          ? (e: CustomEvent<{ chatMessageId: string }>) =>
              this.requestVideo(e.detail.chatMessageId)
          : undefined}
        @start-call=${this.startCall}
        @show-toast=${this.handleShowToast}
        @audio-started=${props.live
          ? () => {
              this.mediaController.stopLiveCard(this.shadowRoot);
              this.currentVideoFor = undefined;
            }
          : undefined}
      >
        <span slot="mic-icon">${micIcon(25, "#6C7E85")}</span>
        <span slot="wave-icon">${waveIcon(20)}</span>
        <span slot="send-icon">${sendIcon(20)}</span>
      </hollo-landing-gradient-minimalist>`;
    }

    return html`<hollo-landing-default
      .live=${props.live ?? false}
      .fileId=${props.fileId ?? ""}
      .alt=${props.alt ?? ""}
      .fullName=${props.fullName ?? ""}
      .currentVideoFor=${props.currentVideoFor}
      .profileImageId=${props.profileImageId ?? ""}
      .messages=${props.messages}
      .typing=${props.typing}
      @send-text=${(e: CustomEvent<{ text: string }>) => this.handleSendText(e)}
      @send-audio=${(e: CustomEvent<{ base64: string; mimeType: string }>) =>
        this.handleSendAudio(e)}
      @request-audio=${(e: CustomEvent<{ chatMessageId: string }>) =>
        this.requestAudio(e.detail.chatMessageId)}
      @request-video=${props.live
        ? (e: CustomEvent<{ chatMessageId: string }>) =>
            this.requestVideo(e.detail.chatMessageId)
        : undefined}
      @start-call=${this.startCall}
      @show-toast=${this.handleShowToast}
      @audio-started=${props.live
        ? () => {
            this.mediaController.stopLiveCard(this.shadowRoot);
            this.currentVideoFor = undefined;
          }
        : undefined}
    >
      <span slot="mic-icon">${micIcon(25, "#6C7E85")}</span>
      <span slot="wave-icon">${waveIcon(20)}</span>
      <span slot="send-icon">${sendIcon(20)}</span>
    </hollo-landing-default>`;
  }

  private renderContentBody() {
    if (this.firstLoad && !this.user && !this.userLoadError)
      return html`<div
        class="hollo-spinner-wrap"
        role="status"
        aria-label="Loading"
      >
        <div class="hollo-spinner"></div>
        <div class="hollo-spinner-text">Connecting...</div>
      </div>`;
    if (this.userLoadError) {
      const errorInfo = this.formatErrorMessage(this.userLoadError);
      return html`<div class="hollo-error-panel" role="alert">
        <div class="hollo-error-icon-wrapper">
          ${errorIcon(48)}
        </div>
        <div class="hollo-error-content">
          <h2 class="hollo-error-title">${errorInfo.title}</h2>
          <p class="hollo-error-desc">${errorInfo.description}</p>
        </div>
      </div>`;
    }

    if (this.liveCharacter)
      return html`<div class="hollo-live-layout live-hollo-content">
        <hollo-live-card
          .fileId=${this.user?.currentProfileImage}
          .alt=${this.user?.firstName}
          .videoSrc=${this.liveVideoSrc}
          .loop=${this.liveLoop}
          .loading=${this.liveVideoLoading}
          .failed=${this.liveVideoFailed}
          .reconnecting=${this.reconnecting}
          @live-video-ended=${this.resetToIdleVideo}
          @load-idle-video=${this.loadIdleVideo}
          @send-audio=${(
            e: CustomEvent<{ base64: string; mimeType: string }>
          ) => this.handleSendAudio(e)}
        ></hollo-live-card>

        ${this.renderLandingComponent(
          normalizeEmbedStyle(this.user?.embedStyle),
          {
            live: true,
            fileId: this.user?.currentProfileImage,
            alt: this.user?.firstName,
            currentVideoFor: this.currentVideoFor,
            fullName: this.user?.firstName + " " + this.user?.lastName,
            profileImageId: this.user?.currentProfileImage,
            messages: this.messages,
            typing: this.typing || this.typingLocal,
            webcallUrl: this.getSocketUrl(),
          }
        )}
      </div>`;

    return this.renderLandingComponent(
      normalizeEmbedStyle(this.user?.embedStyle),
      {
        fileId: this.user?.currentProfileImage,
        alt: this.user?.firstName,
        fullName: this.user?.firstName + " " + this.user?.lastName,
        currentVideoFor: this.currentVideoFor,
        profileImageId: this.user?.currentProfileImage,
        messages: this.messages,
        typing: this.typing || this.typingLocal,
        webcallUrl: this.getSocketUrl(),
      }
    );
  }

  private renderToasts() {
    if (
      !this.toasts.length ||
      (this.firstLoad && !this.user && !this.userLoadError)
    )
      return null;
    return html`<div class="hollo-toast-container">
      ${this.toasts
        .filter((t) => t.level)
        .map(
          (t) => html`<div
            class="hollo-toast ${t.level === "warning"
              ? "hollo-toast--warning"
              : t.level === "success"
              ? "hollo-toast--success"
              : "hollo-toast--error"}"
          >
            <div class="hollo-toast-icon">
              ${t.level === "warning"
                ? warningIcon(16)
                : t.level === "success"
                ? successIcon(16)
                : errorIcon(16)}
            </div>
            <div class="hollo-toast-content">
              <div class="hollo-toast-text">${t.text}</div>
              ${t.count > 1
                ? html`<span class="hollo-toast-count">×${t.count}</span>`
                : null}
            </div>
            <button
              class="hollo-toast-close"
              aria-label="Dismiss"
              @click=${() => this.dismissToast(t.id)}
            >
              ${xIcon(16, "currentColor")}
            </button>
          </div>`
        )}
    </div>`;
  }

  private renderCallSheet() {
    if (!this.isCalling) return null;
    return html` <hollo-call-sheet
      .fileId=${this.user?.currentProfileImage}
      .alt=${this.user?.firstName + " " + this.user?.lastName}
      .seconds=${this.callSeconds}
      .wsUrl=${this.getSocketUrl()}
      .to=${this.user?.twinPhoneNumber || this.user?.id || ""}
      .language=${this.language}
      @end-call=${this.endCall}
      @call-connected=${this.onCallConnected}
    >
      <span slot="mic-icon">${micIconSvg(27, "white", 1)}</span>
      <span slot="mic-slash-icon">${micSlashIcon(27)}</span>
      <span slot="speaker-icon">${speakerIcon(27)}</span>
      <span slot="end-icon">${endIcon(27)}</span>
    </hollo-call-sheet>`;
  }

  private renderPopup() {
    if (!this.isOpen) return null;
    return html` <div
      class="hollo-popup ${this.liveCharacter
        ? "live-hollo-popup-width"
        : ""} ${normalizeEmbedStyle(this.user?.embedStyle) ===
      "gradient_minimalist"
        ? "hollo-popup-gradient-minimalist"
        : ""}"
      role="dialog"
      aria-label="Hollo popup"
    >
      ${this.user && this.user.isEmbedEnabled === false
        ? html`<div class="hollo-content">
            <div class="hollo-error-panel" role="alert">
              <div class="hollo-error-icon-wrapper">
                ${errorIcon(48)}
              </div>
              <div class="hollo-error-content">
                <h2 class="hollo-error-title">Access Restricted</h2>
                <p class="hollo-error-desc">
                  This chat widget is not available for your account. Please contact support if you believe this is an error.
                </p>
              </div>
            </div>
          </div>`
        : html`
            ${this.renderHeaderAndMenus()} ${this.renderConnectionBanner()}
            <div class="hollo-content ${this.liveCharacter ? "" : ""}">
              ${this.renderContentBody()}
            </div>
            ${this.renderToasts()} ${this.renderCallSheet()}
          `}
    </div>`;
  }

  protected updated(changed: Map<string, unknown>) {
    super.updated(changed);
    // Track viewport size to toggle mobile behaviors
    try {
      this.updateIsMobile();
    } catch (e) {
      console.error("Error updating mobile state:", e);
    }
  }

  render() {
    lockBodyScroll(this.isMobile && this.isOpen);
    const customAgentClass =
      this.agentId === "8236076d-d665-409f-a8ba-b89b25f332e5"
        ? "hollo-agent-pps"
        : this.agentId === "0762a5c3-4e48-4103-90e7-18974a39dd96"
        ? "hollo-agent-mike-torchia"
        : "";
    return html`
      <div class="hollo-widget ${customAgentClass}">
        ${this.renderTrigger()} ${this.renderNudge()} ${this.renderPopup()}
      </div>
    `;
  }

  static get styles() {
    // Both style sets are loaded. Since they're standalone (no attribute selectors),
    // gradient-minimalist styles will override default styles where they differ.
    return [
      holloEmbedStyles,
      holloEmbedStylesGradientMinimalist,
      customUserPpsStyles,
      customUserMikeTorchiaStyles,
    ];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "hollo-embed": HolloEmbed;
  }
}
